import React from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from "remotion";
import type { Captions } from "@veyso/core";

/**
 * Captions are drawn as components, never burned into the source with ffmpeg,
 * so toggling or restyling them is a timeline edit rather than a re-encode.
 * Cue times are on the finished video's clock, so this reads the global frame.
 */
export const CaptionLayer: React.FC<{ captions: Captions }> = ({ captions }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  if (!captions.enabled || captions.cues.length === 0) return null;

  const time = frame / fps;
  const cue = captions.cues.find((candidate) => time >= candidate.start && time < candidate.end);
  if (!cue) return null;

  const style = captions.style;
  const justify = style.position === "top" ? "flex-start" : style.position === "center" ? "center" : "flex-end";
  const padding = style.position === "center" ? 0 : `${style.offset * 100}%`;

  const words = style.wordHighlight ? cue.words : undefined;
  // A translated cue's words carry their own spaces (Japanese and Thai have
  // none to add); the English ones are bare words, spaced here.
  const spaced = !words || words.map((word) => word.text).join("") !== cue.text;

  return (
    <AbsoluteFill style={{ justifyContent: justify, alignItems: "center", pointerEvents: "none" }}>
      <div
        // Arabic reads right to left: without this its full stop lands at the start.
        dir={captions.direction ?? "auto"}
        style={{
          maxWidth: `${style.maxWidth * 100}%`,
          margin: style.position === "top" ? `${padding} 0 0 0` : `0 0 ${padding} 0`,
          padding: "0.4em 0.7em",
          borderRadius: 12,
          backgroundColor: style.backgroundColor,
          color: style.color,
          fontFamily: style.fontFamily,
          fontSize: style.fontSize,
          fontWeight: style.fontWeight,
          lineHeight: 1.32,
          textAlign: "center",
          textWrap: "balance",
        }}
      >
        {words && words.length > 0
          ? words.map((word, index) => (
              <span
                key={`${cue.id}-${index}`}
                style={{
                  color: time >= word.start && time < word.end ? style.highlightColor : style.color,
                  transition: "none",
                }}
              >
                {style.uppercase ? word.text.toUpperCase() : word.text}
                {spaced && index < words.length - 1 ? " " : ""}
              </span>
            ))
          : style.uppercase
            ? cue.text.toUpperCase()
            : cue.text}
      </div>
    </AbsoluteFill>
  );
};
